import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Eye, CalendarCheck, ArrowUpRight, RefreshCcw, ArrowLeft, Globe, FileText, Clock, Trash2, Bell, CheckCircle2, Circle, Check, Wallet, Landmark, PhoneCall, PhoneOff, ShieldCheck, Coins, X, UserX, RotateCcw, Pencil, CreditCard, Link as LinkIcon } from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "winfield_admin_token";

// Structured verification checklist — every point is mandatory before signing unlocks.
const VERIFICATION_POINTS = [
  { key: "full_name", label: "Full name" },
  { key: "date_of_birth", label: "Date of birth" },
  { key: "passport_id_number", label: "Passport / ID number" },
  { key: "address", label: "Address" },
  { key: "country", label: "Country" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone number (used for OTP identity confirmation)" },
];
const UNDERSTANDING_POINTS = [
  { key: "not_owner", label: "Buyer is NOT acquiring the apartment — Winfield remains the registered owner" },
  { key: "licence_conditional", label: "The licence has not been granted — token issuance depends on it" },
  { key: "rights_from_occupancy", label: "Rights begin from the occupancy permit + full furnishing, not from signing" },
  { key: "package_scope", label: "What one package is, and that more can be bought" },
  { key: "instalment_schedule", label: "The instalment schedule and what happens if a payment is missed" },
  { key: "withdrawal_refund", label: "The withdrawal window and the refund guarantee" },
];
const BUYER_FIELDS = [
  { key: "full_name", label: "Full name" },
  { key: "date_of_birth", label: "Date of birth" },
  { key: "passport_id_number", label: "Passport / ID number" },
  { key: "address", label: "Address" },
  { key: "country", label: "Country" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
];


const getToken = () =>
  typeof window !== "undefined" ? window.sessionStorage.getItem(TOKEN_KEY) : null;

const setToken = (t) => {
  if (typeof window !== "undefined") {
    if (t) window.sessionStorage.setItem(TOKEN_KEY, t);
    else window.sessionStorage.removeItem(TOKEN_KEY);
  }
};

const apiFetch = async (path, opts = {}) => {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  return res;
};

// ---------- Login ----------
const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setError("");
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Login failed");
      } else {
        setToken(data.token);
        onLogin();
      }
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAF7]">
      <form
        onSubmit={onSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10 wf-glow"
      >
        <div className="flex items-center gap-3">
          <img src="/img/winn-coin.png" alt="Winfield" className="w-10 h-10 object-contain" />
          <span className="font-heading font-semibold text-lg tracking-tight">Winfield Admin</span>
        </div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight mt-6">Sign in</h1>
        <p className="text-sm text-[#62646B] mt-2">Restricted area. Single-admin access.</p>

        <div className="mt-7 space-y-4">
          <div>
            <label className="text-xs text-[#62646B] block mb-1.5">Username</label>
            <input
              data-testid="admin-input-username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="text-xs text-[#62646B] block mb-1.5">Password</label>
            <input
              data-testid="admin-input-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full px-6 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </div>

        <a
          href="/"
          className="block text-center text-xs text-[#62646B] hover:text-[#0A0A0F] mt-6 inline-flex items-center justify-center gap-1.5 w-full"
        >
          <ArrowLeft size={12} /> Back to site
        </a>
      </form>
    </div>
  );
};

// ---------- Helpers ----------
// Convert ISO country code to flag emoji using regional indicator symbols.
const countryFlag = (code) => {
  if (!code || code.length !== 2 || code === "ZZ") return "🏳️";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + code.toUpperCase().charCodeAt(0) - 65,
    A + code.toUpperCase().charCodeAt(1) - 65
  );
};

const formatShortDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const ChartTooltip = ({ active, payload, label, suffix = "" }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl border border-[#E8E7E2] bg-white px-3 py-2 shadow-sm text-xs">
      <p className="font-semibold text-[#0A0A0F]">{label}{suffix}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="tabular-nums">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// ---------- Stat card ----------
const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#E8E7E2] rounded-2xl p-6"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest text-[#62646B] font-semibold">{label}</span>
      <Icon size={16} className="text-[#62646B]" />
    </div>
    <p className="font-heading text-3xl font-bold mt-3">{value}</p>
    {sub && <p className="text-xs text-[#62646B] mt-1.5">{sub}</p>}
  </div>
);

// ---------- Notification helpers ----------
const SEEN_PA_KEY = "winfield_admin_seen_pa";
const SEEN_RES_KEY = "winfield_admin_seen_res";

const getSeen = (key) => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(key);
};
const setSeen = (key, value) => {
  if (typeof window !== "undefined") window.localStorage.setItem(key, value);
};
const countNewerThan = (rows, getTs, lastSeen) => {
  if (!Array.isArray(rows) || rows.length === 0) return { count: 0, latest: null };
  let count = 0;
  let latest = null;
  for (const r of rows) {
    const ts = getTs(r);
    if (!ts) continue;
    if (!latest || ts > latest) latest = ts;
    if (!lastSeen || ts > lastSeen) count += 1;
  }
  return { count, latest };
};

// ---------- Dashboard ----------
const Dashboard = ({ onLogout }) => {
  const [analytics, setAnalytics] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [preAgreements, setPreAgreements] = useState([]);
  const [capacity, setCapacity] = useState(null);   // {unit, capacity, taken, remaining, sold_out}
  const [capDraft, setCapDraft] = useState("");
  const [savingCap, setSavingCap] = useState(false);
  const [paFilter, setPaFilter] = useState("generated"); // "generated" | "drafts" | "all"
  const [propFilter, setPropFilter] = useState("all");    // "all" | unit string
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);
  const [seenPa, setSeenPaState] = useState(() => getSeen(SEEN_PA_KEY));
  const [seenRes, setSeenResState] = useState(() => getSeen(SEEN_RES_KEY));
  const notifRef = useRef(null);

  const paStats = useMemo(
    () => countNewerThan(preAgreements, (r) => r.created_at, seenPa),
    [preAgreements, seenPa],
  );
  const draftCount = useMemo(
    () => preAgreements.filter((r) => r.status === "draft").length,
    [preAgreements],
  );
  const generatedCount = useMemo(
    () => preAgreements.filter((r) => r.status !== "draft").length,
    [preAgreements],
  );
  const allUnits = useMemo(() => {
    const set = new Set(["129", "141"]);
    preAgreements.forEach((r) => { if (r.apartment_unit) set.add(String(r.apartment_unit)); });
    return [...set].sort();
  }, [preAgreements]);
  const filteredPa = useMemo(() => {
    let list = preAgreements;
    if (paFilter === "drafts") list = list.filter((r) => r.status === "draft");
    else if (paFilter !== "all") list = list.filter((r) => r.status !== "draft");
    if (propFilter !== "all") list = list.filter((r) => String(r.apartment_unit) === propFilter);
    return list;
  }, [preAgreements, paFilter, propFilter]);
  const resStats = useMemo(
    () => countNewerThan(
      reservations,
      (r) => (typeof r.timestamp === "string" ? r.timestamp : (r.timestamp ? new Date(r.timestamp).toISOString() : null)),
      seenRes,
    ),
    [reservations, seenRes],
  );
  const unread = paStats.count + resStats.count;

  // Update browser tab title with unread count
  useEffect(() => {
    if (typeof document === "undefined") return;
    const base = "Winfield Admin";
    document.title = unread > 0 ? `(${unread}) ${base}` : base;
  }, [unread]);

  // Close notif dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const onClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [notifOpen]);

  const markAllRead = () => {
    if (paStats.latest) {
      setSeen(SEEN_PA_KEY, paStats.latest);
      setSeenPaState(paStats.latest);
    }
    if (resStats.latest) {
      setSeen(SEEN_RES_KEY, resStats.latest);
      setSeenResState(resStats.latest);
    }
    setNotifOpen(false);
  };

  const jumpToSection = (testid) => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setNotifOpen(false);
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [aRes, rRes, pRes] = await Promise.all([
        apiFetch("/api/admin/analytics"),
        apiFetch("/api/admin/reservations"),
        apiFetch("/api/admin/pre-agreements"),
      ]);
      if (aRes.status === 401 || rRes.status === 401 || pRes.status === 401) {
        onLogout();
        return;
      }
      const a = await aRes.json();
      const r = await rRes.json();
      const p = await pRes.json();
      setAnalytics(a);
      setReservations(r);
      setPreAgreements(Array.isArray(p) ? p : []);
      try {
        const cRes = await apiFetch("/api/admin/capacity");
        if (cRes.ok) {
          const c = await cRes.json();
          setCapacity(c);
          setCapDraft(String(c.capacity));
        }
      } catch { /* non-fatal */ }
    } catch (e) {
      setError("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const selectCapUnit = async (u) => {
    try {
      const res = await apiFetch(`/api/admin/capacity?unit=${encodeURIComponent(u)}`);
      if (res.ok) {
        const c = await res.json();
        setCapacity(c);
        setCapDraft(String(c.capacity));
      }
    } catch { /* non-fatal */ }
  };

  const saveCapacity = async () => {
    const n = parseInt(capDraft, 10);
    if (Number.isNaN(n) || n < 0) {
      alert("Capacity must be a non-negative whole number.");
      return;
    }
    setSavingCap(true);
    try {
      const res = await apiFetch("/api/admin/capacity", {
        method: "POST",
        body: JSON.stringify({ unit: capacity?.unit || "129", capacity: n }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(`Failed to update capacity: ${d.detail || res.statusText}`);
        return;
      }
      const c = await res.json();
      setCapacity(c);
      setCapDraft(String(c.capacity));
    } finally {
      setSavingCap(false);
    }
  };

  const deletePreAgreement = async (row) => {
    const ok = window.confirm(
      `Delete pre-agreement ${row.contract_number}?\n\nThis removes the database record AND the signed PDF from disk. The contract number itself cannot be re-issued (the WI-RZ counter is sequential).\n\nThis action cannot be undone.`,
    );
    if (!ok) return;
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}`, { method: "DELETE" });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(`Delete failed: ${data.detail || res.statusText}`);
      return;
    }
    setPreAgreements((cur) => cur.filter((x) => x.id !== row.id));
  };

  const toggleReviewed = async (row) => {
    const next = !row.reviewed;
    // Optimistic update
    setPreAgreements((cur) =>
      cur.map((x) =>
        x.id === row.id
          ? { ...x, reviewed: next, reviewed_at: next ? new Date().toISOString() : null }
          : x,
      ),
    );
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}/review`, {
      method: "POST",
      body: JSON.stringify({ reviewed: next }),
    });
    if (!res.ok) {
      // Revert on failure
      setPreAgreements((cur) =>
        cur.map((x) => (x.id === row.id ? { ...x, reviewed: row.reviewed, reviewed_at: row.reviewed_at } : x)),
      );
      const data = await res.json().catch(() => ({}));
      alert(`Failed to update: ${data.detail || res.statusText}`);
    }
  };

  const confirmSepa = async (row) => {
    const ok = window.confirm(
      `Confirm the SEPA transfer for ${row.contract_number} has landed?\n\nThis issues the Advance Invoice (SEPA) and marks the reservation paid.`,
    );
    if (!ok) return;
    const res = await apiFetch(`/api/admin/sepa/confirm`, {
      method: "POST",
      body: JSON.stringify({ pre_agreement_id: row.id }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(`Failed to confirm: ${d.detail || res.statusText}`);
      return;
    }
    await load();
  };

  const [contactModal, setContactModal] = useState(null); // row being contacted
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  const copyReservationLink = async (row) => {
    const link = `${window.location.origin}/reserve?id=${row.id}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt("Copy this reservation link:", link);
    }
    setCopiedLinkId(row.id);
    setTimeout(() => setCopiedLinkId((cur) => (cur === row.id ? null : cur)), 1800);
  };

  const [contactConductedBy, setContactConductedBy] = useState("Milovan Lučić");
  const [contactCallLang, setContactCallLang] = useState("en");
  const [contactPayMethod, setContactPayMethod] = useState("");
  const [contactVerif, setContactVerif] = useState({});
  const [contactUnderstand, setContactUnderstand] = useState({});
  const [contactSaving, setContactSaving] = useState(false);

  const [buyerModal, setBuyerModal] = useState(null);
  const [buyerForm, setBuyerForm] = useState({});
  const [buyerSaving, setBuyerSaving] = useState(false);

  const openContact = (row) => {
    setContactConductedBy("Milovan Lučić");
    setContactCallLang(row.language === "sr" ? "sr" : "en");
    setContactPayMethod("");
    setContactVerif({});
    setContactUnderstand({});
    setContactModal(row);
    // Stamp call start (server clock) so duration is auto-computed on completion.
    apiFetch(`/api/admin/pre-agreements/${row.id}/contact/start`, { method: "POST" }).catch(() => {});
  };

  const allContactChecked =
    !!contactPayMethod &&
    VERIFICATION_POINTS.every((p) => contactVerif[p.key]) &&
    UNDERSTANDING_POINTS.every((p) => contactUnderstand[p.key]);

  const submitContact = async () => {
    if (!contactModal) return;
    if (!contactConductedBy.trim()) { alert("Please enter who conducted the call."); return; }
    if (!allContactChecked) { alert("Every checklist point (and the payment method) must be confirmed before signing can unlock."); return; }
    setContactSaving(true);
    try {
      const res = await apiFetch(`/api/admin/pre-agreements/${contactModal.id}/contact`, {
        method: "POST",
        body: JSON.stringify({
          conducted_by: contactConductedBy.trim(),
          language: contactCallLang,
          payment_method: contactPayMethod,
          verification: contactVerif,
          understanding: contactUnderstand,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(`Failed: ${data.detail || res.statusText}`); return; }
      setPreAgreements((cur) => cur.map((x) => (x.id === contactModal.id ? { ...x, status: "contacted", contact: data.contact } : x)));
      setContactModal(null);
    } finally {
      setContactSaving(false);
    }
  };

  const revertContact = async (row) => {
    if (!window.confirm(`Revert ${row.contract_number} to draft?\n\nThis re-locks signing until the verification call is completed again.`)) return;
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}/revert-contact`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(`Failed: ${data.detail || res.statusText}`); return; }
    setPreAgreements((cur) => cur.map((x) => (x.id === row.id ? { ...x, status: "draft" } : x)));
  };

  const markUnreachable = async (row) => {
    const note = window.prompt(`Log a no-answer / no-show for ${row.contract_number} (optional note):`, "");
    if (note === null) return;
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}/mark-unreachable`, {
      method: "POST", body: JSON.stringify({ note: note || "" }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(`Failed: ${data.detail || res.statusText}`); return; }
    setPreAgreements((cur) => cur.map((x) => (x.id === row.id ? { ...x, status: "unreachable", contact: { ...(x.contact || {}), attempts: data.attempts } } : x)));
  };

  const markDeclined = async (row) => {
    if (!window.confirm(`Mark ${row.contract_number} as DECLINED?\n\nThe buyer chose not to proceed. The record is kept for audit and can be reopened later.`)) return;
    const reason = window.prompt("Reason (optional):", "") || "";
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}/mark-declined`, {
      method: "POST", body: JSON.stringify({ reason }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(`Failed: ${data.detail || res.statusText}`); return; }
    setPreAgreements((cur) => cur.map((x) => (x.id === row.id ? { ...x, status: "declined" } : x)));
  };

  const reopenReservation = async (row) => {
    if (!window.confirm(`Reopen ${row.contract_number} back to draft?`)) return;
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}/reopen`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(`Failed: ${data.detail || res.statusText}`); return; }
    setPreAgreements((cur) => cur.map((x) => (x.id === row.id ? { ...x, status: "draft" } : x)));
  };

  const openBuyerEdit = async (row) => {
    setBuyerModal({ ...row, _loading: true });
    setBuyerForm({});
    try {
      const res = await apiFetch(`/api/admin/pre-agreements/${row.id}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(`Failed to load buyer: ${data.detail || res.statusText}`); setBuyerModal(null); return; }
      setBuyerForm({ ...(data.buyer || {}) });
      setBuyerModal({ ...row, _loading: false });
    } catch {
      setBuyerModal(null);
    }
  };

  const submitBuyerEdit = async () => {
    if (!buyerModal) return;
    setBuyerSaving(true);
    try {
      const res = await apiFetch(`/api/admin/pre-agreements/${buyerModal.id}/buyer`, {
        method: "POST", body: JSON.stringify(buyerForm),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(`Failed: ${data.detail || res.statusText}`); return; }
      const b = data.buyer || {};
      setPreAgreements((cur) => cur.map((x) => (x.id === buyerModal.id
        ? { ...x, buyer_full_name: b.full_name, buyer_email: b.email, buyer_phone: b.phone } : x)));
      setBuyerModal(null);
    } finally {
      setBuyerSaving(false);
    }
  };

  const issueTokens = async (row) => {
    if (!window.confirm(`Mark $WINN tokens as ISSUED for ${row.contract_number}?\n\nOnly do this once issuance is approved and complete. This is a terminal state.`)) return;
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}/issue-tokens`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(`Failed: ${data.detail || res.statusText}`); return; }
    setPreAgreements((cur) => cur.map((x) => (x.id === row.id ? { ...x, status: "tokens_issued" } : x)));
  };

  const togglePaid = async (row) => {
    const wasPaid = row.status === "paid";
    const next = !wasPaid;
    // Optimistic
    setPreAgreements((cur) =>
      cur.map((x) =>
        x.id === row.id
          ? {
              ...x,
              status: next ? "paid" : (x.signed_at ? "signed" : "draft"),
              paid_at: next ? new Date().toISOString() : null,
            }
          : x,
      ),
    );
    const res = await apiFetch(`/api/admin/pre-agreements/${row.id}/payment-status`, {
      method: "POST",
      body: JSON.stringify({ paid: next }),
    });
    if (!res.ok) {
      // Revert
      setPreAgreements((cur) =>
        cur.map((x) => (x.id === row.id ? { ...x, status: row.status, paid_at: row.paid_at } : x)),
      );
      const data = await res.json().catch(() => ({}));
      alert(`Failed to update: ${data.detail || res.statusText}`);
      return;
    }
    const data = await res.json();
    // Sync the real status returned by the server
    setPreAgreements((cur) =>
      cur.map((x) =>
        x.id === row.id ? { ...x, status: data.status, paid_at: data.paid_at, paid_by: data.paid_by } : x,
      ),
    );
  };

  const deleteReservation = async (row) => {
    const ok = window.confirm(
      `Delete reservation from ${row.name || row.email}?\n\nThis action cannot be undone.`,
    );
    if (!ok) return;
    const res = await apiFetch(`/api/admin/reservations/${row.id}`, { method: "DELETE" });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(`Delete failed: ${data.detail || res.statusText}`);
      return;
    }
    setReservations((cur) => cur.filter((x) => x.id !== row.id));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First load: if user has no "seen" timestamp yet, treat current latest as seen
  // so they don't get bombarded with "all historical entries are new".
  useEffect(() => {
    if (!loading && paStats.latest && !seenPa) {
      setSeen(SEEN_PA_KEY, paStats.latest);
      setSeenPaState(paStats.latest);
    }
    if (!loading && resStats.latest && !seenRes) {
      setSeen(SEEN_RES_KEY, resStats.latest);
      setSeenResState(resStats.latest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F]">
      <header className="border-b border-[#E8E7E2] bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/img/winn-coin.png" alt="Winfield" className="w-9 h-9 object-contain shrink-0" />
            <span className="font-heading font-semibold tracking-tight hidden sm:inline">Winfield Admin</span>
            <span className="font-heading font-semibold tracking-tight sm:hidden">Admin</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Notification bell */}
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                onClick={() => setNotifOpen((v) => !v)}
                data-testid="admin-notif-bell"
                aria-label={unread > 0 ? `${unread} new submissions` : "No new submissions"}
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E8E7E2] hover:border-[#0A0A0F] text-[#0A0A0F] transition-colors"
              >
                <Bell size={16} />
                {unread > 0 && (
                  <span
                    data-testid="admin-notif-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div
                  data-testid="admin-notif-dropdown"
                  className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-white border border-[#E8E7E2] rounded-2xl shadow-lg p-2 z-20"
                >
                  <div className="px-3 pt-2 pb-2 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-[#62646B] font-semibold">Notifications</span>
                    {unread > 0 && (
                      <button
                        type="button"
                        onClick={markAllRead}
                        data-testid="admin-notif-mark-read"
                        className="text-xs text-[#0A0A0F] hover:underline font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {unread === 0 ? (
                    <p className="px-3 pb-3 text-sm text-[#62646B]">You're all caught up.</p>
                  ) : (
                    <ul className="space-y-1">
                      {paStats.count > 0 && (
                        <li>
                          <button
                            type="button"
                            onClick={() => jumpToSection("admin-pre-agreements")}
                            data-testid="admin-notif-pa"
                            className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-[#FAFAF7] flex items-center gap-3"
                          >
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#0A0A0F] text-white shrink-0">
                              <FileText size={15} />
                            </span>
                            <span className="flex-1">
                              <span className="block text-sm font-semibold">{paStats.count} new pre-agreement{paStats.count === 1 ? "" : "s"}</span>
                              <span className="block text-xs text-[#62646B]">Signed or in progress</span>
                            </span>
                          </button>
                        </li>
                      )}
                      {resStats.count > 0 && (
                        <li>
                          <button
                            type="button"
                            onClick={() => jumpToSection("admin-reservations")}
                            data-testid="admin-notif-res"
                            className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-[#FAFAF7] flex items-center gap-3"
                          >
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#0A0A0F] text-white shrink-0">
                              <CalendarCheck size={15} />
                            </span>
                            <span className="flex-1">
                              <span className="block text-sm font-semibold">{resStats.count} new reservation{resStats.count === 1 ? "" : "s"}</span>
                              <span className="block text-xs text-[#62646B]">Quick-interest form submissions</span>
                            </span>
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                  <p className="px-3 pt-2 pb-1 text-[10px] text-[#9B9DA4] border-t border-[#E8E7E2] mt-2">
                    Auto-refreshes every 30 seconds
                  </p>
                </div>
              )}
            </div>

            <button
              data-testid="admin-refresh"
              onClick={load}
              aria-label="Refresh"
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-[#E8E7E2] hover:border-[#0A0A0F] text-sm font-medium transition-colors"
            >
              <RefreshCcw size={14} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              aria-label="Logout"
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-sm font-semibold transition-colors"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {loading && <p className="text-sm text-[#62646B]">Loading…</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {analytics && (
          <>
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Overview</h2>
            <div data-testid="admin-stats" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
              <Stat
                testid="stat-pageviews"
                icon={Eye}
                label="Pageviews"
                value={analytics.totals.pageviews.toLocaleString()}
                sub={`${analytics.totals.pageviews_7d} in last 7d · ${analytics.totals.pageviews_30d} in last 30d`}
              />
              <Stat
                testid="stat-sessions"
                icon={Users}
                label="Unique sessions"
                value={analytics.totals.sessions_total.toLocaleString()}
                sub={`${analytics.totals.sessions_7d} in last 7d`}
              />
              <Stat
                testid="stat-reservations"
                icon={CalendarCheck}
                label="Reservations"
                value={analytics.totals.reservations_total.toLocaleString()}
                sub={`${analytics.totals.reservations_7d} in last 7d · ${analytics.totals.reservations_30d} in last 30d`}
              />
              <Stat
                testid="stat-conversion"
                icon={ArrowUpRight}
                label="Conversion"
                value={
                  analytics.totals.sessions_total > 0
                    ? `${((analytics.totals.reservations_total / analytics.totals.sessions_total) * 100).toFixed(1)}%`
                    : "—"
                }
                sub="Reservations / unique sessions"
              />
            </div>

            {/* Daily traffic chart */}
            <section
              data-testid="admin-daily-chart"
              className="bg-white border border-[#E8E7E2] rounded-2xl p-6 mt-10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg font-semibold">Site traffic</h3>
                  <p className="text-xs text-[#62646B] mt-1">Daily pageviews and unique sessions (UTC)</p>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">Last 30 days</span>
              </div>
              <div className="mt-6" style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart data={analytics.daily_series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0A0A0F" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="#0A0A0F" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8A8F9C" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#8A8F9C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E8E7E2" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatShortDate}
                      stroke="#9B9DA4"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: "#E8E7E2" }}
                      minTickGap={20}
                    />
                    <YAxis
                      stroke="#9B9DA4"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: "#E8E7E2" }}
                      allowDecimals={false}
                      width={32}
                    />
                    <Tooltip content={<ChartTooltip />} labelFormatter={formatShortDate} cursor={{ stroke: "#0A0A0F", strokeOpacity: 0.15 }} />
                    <Area
                      type="monotone"
                      dataKey="views"
                      name="Views"
                      stroke="#0A0A0F"
                      strokeWidth={2}
                      fill="url(#gradViews)"
                    />
                    <Area
                      type="monotone"
                      dataKey="sessions"
                      name="Sessions"
                      stroke="#8A8F9C"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      fill="url(#gradSessions)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-5 mt-4 text-xs text-[#62646B]">
                <span className="inline-flex items-center gap-2"><span className="inline-block w-3 h-[2px] bg-[#0A0A0F]" /> Pageviews</span>
                <span className="inline-flex items-center gap-2"><span className="inline-block w-3 h-[2px] bg-[#8A8F9C]" style={{ borderTop: "1px dashed #8A8F9C", background: "transparent" }} /> Unique sessions</span>
              </div>
            </section>

            {/* Hour distribution */}
            <section
              data-testid="admin-hour-chart"
              className="bg-white border border-[#E8E7E2] rounded-2xl p-6 mt-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-[#62646B]" />
                  <h3 className="font-heading text-lg font-semibold">Hour of day</h3>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">Last 7 days · UTC</span>
              </div>
              <div className="mt-6" style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={analytics.hour_distribution} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#E8E7E2" vertical={false} />
                    <XAxis
                      dataKey="hour"
                      stroke="#9B9DA4"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: "#E8E7E2" }}
                      interval={1}
                      tickFormatter={(h) => `${h}h`}
                    />
                    <YAxis
                      stroke="#9B9DA4"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: "#E8E7E2" }}
                      allowDecimals={false}
                      width={32}
                    />
                    <Tooltip
                      content={<ChartTooltip suffix=":00 UTC" />}
                      labelFormatter={(h) => `${h}`}
                      cursor={{ fill: "#F4F2EC" }}
                    />
                    <Bar dataKey="views" name="Views" fill="#0A0A0F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-6">
              {/* Top sources */}
              <section data-testid="admin-top-sources" className="bg-white border border-[#E8E7E2] rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-semibold">Top traffic sources</h3>
                  <span className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">Last 30 days</span>
                </div>
                {analytics.top_sources.length === 0 ? (
                  <p className="text-sm text-[#62646B] mt-6">No traffic yet.</p>
                ) : (
                  <table className="w-full mt-5 text-sm">
                    <thead className="text-xs uppercase tracking-widest text-[#62646B] text-left">
                      <tr>
                        <th className="pb-3 font-semibold">Source</th>
                        <th className="pb-3 font-semibold text-right">Sessions</th>
                        <th className="pb-3 font-semibold text-right">Views</th>
                        <th className="pb-3 font-semibold w-32">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const max = Math.max(...analytics.top_sources.map((s) => s.views));
                        return analytics.top_sources.map((s, i) => (
                          <tr key={i} className="border-t border-[#E8E7E2]">
                            <td className="py-3 font-medium">{s.source}</td>
                            <td className="py-3 text-right tabular-nums">{s.sessions}</td>
                            <td className="py-3 text-right tabular-nums">{s.views}</td>
                            <td className="py-3">
                              <div className="h-2 rounded-full bg-[#F4F2EC] overflow-hidden">
                                <div
                                  className="h-full bg-[#0A0A0F]"
                                  style={{ width: `${(s.views / max) * 100}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
              </section>

              {/* Right column: languages + packages */}
              <div className="space-y-6">
                <section data-testid="admin-languages" className="bg-white border border-[#E8E7E2] rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-semibold">Languages</h3>
                  <ul className="mt-4 space-y-2 text-sm">
                    {analytics.languages.length === 0 && (
                      <li className="text-[#62646B]">No data yet.</li>
                    )}
                    {analytics.languages.map((l, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span className="font-medium uppercase tracking-widest text-xs">{l.language || "—"}</span>
                        <span className="tabular-nums text-[#62646B]">{l.views}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section data-testid="admin-packages" className="bg-white border border-[#E8E7E2] rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-semibold">Package mix</h3>
                  <ul className="mt-4 space-y-2 text-sm">
                    {analytics.packages.length === 0 && (
                      <li className="text-[#62646B]">No reservations yet.</li>
                    )}
                    {analytics.packages.map((p, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span className="font-medium">{p.package}</span>
                        <span className="tabular-nums text-[#62646B]">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </div>

            {/* Countries + Pages */}
            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <section data-testid="admin-top-countries" className="bg-white border border-[#E8E7E2] rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-[#62646B]" />
                    <h3 className="font-heading text-lg font-semibold">Top countries</h3>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">Last 30 days</span>
                </div>
                {analytics.top_countries.length === 0 ? (
                  <p className="text-sm text-[#62646B] mt-6">No traffic yet.</p>
                ) : (
                  <table className="w-full mt-5 text-sm">
                    <thead className="text-xs uppercase tracking-widest text-[#62646B] text-left">
                      <tr>
                        <th className="pb-3 font-semibold">Country</th>
                        <th className="pb-3 font-semibold text-right">Sessions</th>
                        <th className="pb-3 font-semibold text-right">Views</th>
                        <th className="pb-3 font-semibold w-28">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const max = Math.max(...analytics.top_countries.map((c) => c.views));
                        return analytics.top_countries.map((c, i) => (
                          <tr key={i} data-testid={`country-row-${c.country_code || "ZZ"}`} className="border-t border-[#E8E7E2]">
                            <td className="py-3 font-medium">
                              <span className="mr-2 text-base">{countryFlag(c.country_code)}</span>
                              {c.country}
                            </td>
                            <td className="py-3 text-right tabular-nums">{c.sessions}</td>
                            <td className="py-3 text-right tabular-nums">{c.views}</td>
                            <td className="py-3">
                              <div className="h-2 rounded-full bg-[#F4F2EC] overflow-hidden">
                                <div
                                  className="h-full bg-[#0A0A0F]"
                                  style={{ width: `${(c.views / max) * 100}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
              </section>

              <section data-testid="admin-top-pages" className="bg-white border border-[#E8E7E2] rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-[#62646B]" />
                    <h3 className="font-heading text-lg font-semibold">Top pages</h3>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">Last 30 days</span>
                </div>
                {analytics.top_pages.length === 0 ? (
                  <p className="text-sm text-[#62646B] mt-6">No page data yet.</p>
                ) : (
                  <table className="w-full mt-5 text-sm">
                    <thead className="text-xs uppercase tracking-widest text-[#62646B] text-left">
                      <tr>
                        <th className="pb-3 font-semibold">Path</th>
                        <th className="pb-3 font-semibold text-right">Sessions</th>
                        <th className="pb-3 font-semibold text-right">Views</th>
                        <th className="pb-3 font-semibold w-28">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const max = Math.max(...analytics.top_pages.map((p) => p.views));
                        return analytics.top_pages.map((p, i) => (
                          <tr key={i} className="border-t border-[#E8E7E2]">
                            <td className="py-3 font-mono text-xs">{p.path}</td>
                            <td className="py-3 text-right tabular-nums">{p.sessions}</td>
                            <td className="py-3 text-right tabular-nums">{p.views}</td>
                            <td className="py-3">
                              <div className="h-2 rounded-full bg-[#F4F2EC] overflow-hidden">
                                <div
                                  className="h-full bg-[#0A0A0F]"
                                  style={{ width: `${(p.views / max) * 100}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
              </section>
            </div>
          </>
        )}

        {/* Unit capacity / inventory */}
        {capacity && (
          <section data-testid="admin-capacity" className="mt-12">
            <div className="bg-white border border-[#E8E7E2] rounded-2xl p-6">
              <div className="inline-flex rounded-full border border-[#E8E7E2] bg-[#FAFAF7] p-1 mb-5" data-testid="capacity-unit-switch">
                {allUnits.map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => selectCapUnit(u)}
                    data-testid={`capacity-unit-${u}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                      String(capacity.unit) === u
                        ? "bg-[#0A0A0F] text-white"
                        : "text-[#62646B] hover:text-[#0A0A0F]"
                    }`}
                  >
                    Apt. {u}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h2 className="font-heading text-xl font-semibold tracking-tight">
                    Unit {capacity.unit} — availability
                  </h2>
                  <p className="text-sm text-[#62646B] mt-1">
                    A slot is filled once a deposit (or full) payment is confirmed.
                  </p>
                  <p className="mt-3 text-2xl font-semibold" data-testid="capacity-taken">
                    {capacity.taken}
                    <span className="text-[#62646B] text-base font-normal"> of {capacity.capacity} taken</span>
                    {capacity.sold_out ? (
                      <span className="ml-3 align-middle inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FBEDED] text-[#7A1F1F] border border-[#E7B7B7]">
                        SOLD OUT
                      </span>
                    ) : (
                      <span className="ml-3 align-middle inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#F1F7F1] text-[#2F6B33] border border-[#CDE3CD]">
                        {capacity.remaining} left
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <label className="text-xs text-[#62646B] block mb-1.5">Cap</label>
                    <input
                      type="number"
                      min="0"
                      value={capDraft}
                      onChange={(e) => setCapDraft(e.target.value)}
                      data-testid="capacity-input"
                      className="w-24 px-3 py-2 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={saveCapacity}
                    disabled={savingCap || capDraft === String(capacity.capacity)}
                    data-testid="capacity-save"
                    className="px-5 py-2 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingCap ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Pre-agreements table (signed pre-agreements with PDF download) */}
        <section data-testid="admin-pre-agreements" className="mt-12">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Pre-agreements</h2>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-full border border-[#E8E7E2] bg-white p-1" data-testid="pa-filter">
                {[
                  { k: "generated", label: `Generated (${generatedCount})` },
                  { k: "drafts", label: `Drafts (${draftCount})` },
                  { k: "all", label: `All (${preAgreements.length})` },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setPaFilter(opt.k)}
                    data-testid={`pa-filter-${opt.k}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                      paFilter === opt.k
                        ? "bg-[#0A0A0F] text-white"
                        : "text-[#62646B] hover:text-[#0A0A0F]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-[#62646B]">{filteredPa.length} shown</span>
            </div>
          </div>

          <div className="mt-3 inline-flex rounded-full border border-[#E8E7E2] bg-white p-1" data-testid="pa-property-filter">
            {[{ k: "all", label: `All apartments (${preAgreements.length})` }, ...allUnits.map((u) => ({ k: u, label: `Apt. ${u} (${preAgreements.filter((r) => String(r.apartment_unit) === u).length})` }))].map((opt) => (
              <button
                key={opt.k}
                type="button"
                onClick={() => setPropFilter(opt.k)}
                data-testid={`pa-property-${opt.k}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  propFilter === opt.k ? "bg-[#0A0A0F] text-white" : "text-[#62646B] hover:text-[#0A0A0F]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="mt-5 bg-white border border-[#E8E7E2] rounded-2xl overflow-hidden">
            {filteredPa.length === 0 ? (
              <p className="p-6 text-sm text-[#62646B]">
                {paFilter === "drafts"
                  ? "No abandoned drafts."
                  : paFilter === "generated"
                  ? "No generated pre-agreements yet."
                  : "No pre-agreements yet."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAF7] text-xs uppercase tracking-widest text-[#62646B] text-left">
                    <tr>
                      <th className="px-5 py-3 font-semibold">Contract</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold">Paid</th>
                      <th className="px-5 py-3 font-semibold">Buyer</th>
                      <th className="px-5 py-3 font-semibold">Email</th>
                      <th className="px-5 py-3 font-semibold">Phone</th>
                      <th className="px-5 py-3 font-semibold">Apt.</th>
                      <th className="px-5 py-3 font-semibold text-right">Price</th>
                      <th className="px-5 py-3 font-semibold">Lang</th>
                      <th className="px-5 py-3 font-semibold">Created</th>
                      <th className="px-5 py-3 font-semibold">Reviewed</th>
                      <th className="px-5 py-3 font-semibold">PDF</th>
                      <th className="px-5 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPa.map((r) => (
                      <tr
                        key={r.id}
                        data-testid={`pre-agreement-row-${r.contract_number}`}
                        className="border-t border-[#E8E7E2] hover:bg-[#FAFAF7] transition-colors"
                      >
                        <td className="px-5 py-3 font-mono font-semibold">
                          <div className="flex items-center gap-1.5">
                            {r.contract_number}
                            <button
                              type="button"
                              onClick={() => copyReservationLink(r)}
                              data-testid={`pre-agreement-copy-link-${r.contract_number}`}
                              title="Copy the buyer's reservation link (to re-send if they lost it)"
                              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[#62646B] hover:text-[#0A0A0F] hover:bg-[#F4F2EC] transition-colors shrink-0"
                            >
                              {copiedLinkId === r.id ? <Check size={13} className="text-[#2F6B33]" /> : <LinkIcon size={13} />}
                            </button>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                              r.status === "signed" || r.status === "payment_initiated"
                                ? "bg-[#0A0A0F] text-white"
                                : r.status === "draft"
                                ? "bg-[#F4F2EC] text-[#62646B]"
                                : r.status === "unreachable"
                                ? "bg-[#FBF1E3] text-[#8a5a2b]"
                                : r.status === "declined"
                                ? "bg-[#FBEBEB] text-[#8a3a3a]"
                                : "bg-[#F4F2EC] text-[#0A0A0F]"
                            }`}
                          >
                            {(r.status || "").replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {(() => {
                            const st = r.status;
                            const editBtn = (
                              <button
                                type="button"
                                onClick={() => openBuyerEdit(r)}
                                data-testid={`pre-agreement-edit-buyer-${r.contract_number}`}
                                title="Correct buyer data caught on the call (same WI-RZ number, audited)."
                                className="inline-flex items-center gap-1 text-xs text-[#62646B] underline underline-offset-2 hover:text-[#0A0A0F]"
                              >
                                <Pencil size={11} /> Edit details
                              </button>
                            );
                            if (st === "draft" || st === "unreachable") {
                              const attempts = (r.contact || {}).attempts;
                              return (
                                <div className="flex flex-wrap items-center gap-2">
                                  {st === "unreachable" && (
                                    <span
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#FBF1E3] text-[#8a5a2b] border border-[#EAD8BF]"
                                      title={`No answer${attempts ? ` · ${attempts} attempt(s)` : ""}`}
                                      data-testid={`pre-agreement-unreachable-${r.contract_number}`}
                                    >
                                      <PhoneOff size={12} /> No answer{attempts ? ` (${attempts})` : ""}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openContact(r)}
                                    data-testid={`pre-agreement-contact-${r.contract_number}`}
                                    title="Run the verification call checklist — this unlocks signing for the buyer."
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white text-[#0A0A0F] border border-[#E8E7E2] hover:bg-[#0A0A0F] hover:text-white transition-colors"
                                  >
                                    <PhoneCall size={12} /> {st === "unreachable" ? "Retry call" : "Start verification call"}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => markUnreachable(r)}
                                    data-testid={`pre-agreement-mark-unreachable-${r.contract_number}`}
                                    className="text-xs text-[#8a5a2b] underline underline-offset-2 hover:text-amber-800"
                                  >
                                    No answer
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => markDeclined(r)}
                                    data-testid={`pre-agreement-mark-declined-${r.contract_number}`}
                                    className="text-xs text-[#8a3a3a] underline underline-offset-2 hover:text-red-700"
                                  >
                                    Declined
                                  </button>
                                  {editBtn}
                                </div>
                              );
                            }
                            if (st === "declined") {
                              return (
                                <div className="flex items-center gap-2">
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#FBEBEB] text-[#8a3a3a] border border-[#EBCFCF]"
                                    title={(r.contact || {}).decline_reason || "Buyer declined"}
                                    data-testid={`pre-agreement-declined-${r.contract_number}`}
                                  >
                                    <UserX size={12} /> Declined
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => reopenReservation(r)}
                                    data-testid={`pre-agreement-reopen-${r.contract_number}`}
                                    className="inline-flex items-center gap-1 text-xs text-[#62646B] underline underline-offset-2 hover:text-[#0A0A0F]"
                                  >
                                    <RotateCcw size={11} /> Reopen
                                  </button>
                                </div>
                              );
                            }
                            if (st === "contacted") {
                              const c = r.contact || {};
                              const dur = c.duration_seconds != null ? `${Math.round(c.duration_seconds / 60)} min` : "";
                              return (
                                <div className="flex flex-wrap items-center gap-2">
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#F1F7F1] text-[#2F6B33] border border-[#CDE3CD]"
                                    title={`Verified${c.conducted_by ? ' by ' + c.conducted_by : ''}${c.contacted_at ? ' on ' + new Date(c.contacted_at).toLocaleString() : ''}${dur ? ' · ' + dur : ''}${c.payment_method ? ' · pays by ' + (c.payment_method === 'sepa' ? 'SEPA' : 'card') : ''}`}
                                    data-testid={`pre-agreement-contacted-${r.contract_number}`}
                                  >
                                    <ShieldCheck size={12} /> Verified{c.payment_method ? ` · ${c.payment_method === "sepa" ? "SEPA" : "Card"}` : ""}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => revertContact(r)}
                                    data-testid={`pre-agreement-revert-${r.contract_number}`}
                                    className="text-xs text-[#8a3a3a] underline underline-offset-2 hover:text-red-700"
                                  >
                                    Revert
                                  </button>
                                  {editBtn}
                                </div>
                              );
                            }
                            if (st === "awaiting_sepa") {
                              return (
                                <button
                                  type="button"
                                  onClick={() => confirmSepa(r)}
                                  data-testid={`pre-agreement-sepa-confirm-${r.contract_number}`}
                                  title="Confirm the SEPA bank transfer has landed — issues the advance invoice and marks paid."
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white text-amber-700 border border-amber-300 hover:bg-amber-600 hover:text-white transition-colors"
                                >
                                  <Landmark size={12} /> Confirm SEPA
                                </button>
                              );
                            }
                            if (st === "tokens_issued") {
                              return (
                                <span data-testid={`pre-agreement-tokens-${r.contract_number}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#0A0A0F] text-white">
                                  <Coins size={12} /> Tokens issued
                                </span>
                              );
                            }
                            const isPaid = st === "paid";
                            return (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => togglePaid(r)}
                                  data-testid={`pre-agreement-paid-${r.contract_number}`}
                                  aria-pressed={isPaid ? "true" : "false"}
                                  title={isPaid
                                    ? `Marked paid${r.paid_at ? ' on ' + new Date(r.paid_at).toLocaleString() : ''}. Click to revert.`
                                    : "Click once the deposit has landed in the bank account."}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    isPaid
                                      ? "bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700"
                                      : "bg-white text-[#62646B] border border-[#E8E7E2] hover:border-emerald-600 hover:text-emerald-700"
                                  }`}
                                >
                                  {isPaid ? <CheckCircle2 size={12} /> : <Wallet size={12} />}
                                  {isPaid ? "Paid" : "Mark paid"}
                                </button>
                                {isPaid && (
                                  <button
                                    type="button"
                                    onClick={() => issueTokens(r)}
                                    data-testid={`pre-agreement-issue-tokens-${r.contract_number}`}
                                    title="Mark $WINN tokens as issued (only once issuance is approved & complete)."
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white text-[#0A0A0F] border border-[#E8E7E2] hover:bg-[#0A0A0F] hover:text-white transition-colors"
                                  >
                                    <Coins size={12} /> Issue tokens
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-5 py-3 font-medium">{r.buyer_full_name}</td>
                        <td className="px-5 py-3">
                          <a href={`mailto:${r.buyer_email}`} className="text-[#0A0A0F] underline underline-offset-2">
                            {r.buyer_email}
                          </a>
                        </td>
                        <td className="px-5 py-3 text-[#62646B]">{r.buyer_phone || "—"}</td>
                        <td className="px-5 py-3 text-[#62646B]">{r.apartment_unit || "—"}</td>
                        <td className="px-5 py-3 text-right tabular-nums">€{(r.package_price_eur ?? 0).toLocaleString()}</td>
                        <td className="px-5 py-3 uppercase text-xs tracking-widest text-[#62646B]">{r.language}</td>
                        <td className="px-5 py-3 whitespace-nowrap tabular-nums text-[#62646B]">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-5 py-3">
                          <button
                            type="button"
                            onClick={() => toggleReviewed(r)}
                            data-testid={`pre-agreement-review-${r.contract_number}`}
                            aria-pressed={r.reviewed ? "true" : "false"}
                            title={r.reviewed
                              ? `Reviewed${r.reviewed_at ? ' on ' + new Date(r.reviewed_at).toLocaleString() : ''}. Click to mark as unread.`
                              : "Click to mark as reviewed."}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              r.reviewed
                                ? "bg-[#0A0A0F] text-white border border-[#0A0A0F] hover:bg-[#1F1F26]"
                                : "bg-white text-[#62646B] border border-[#E8E7E2] hover:border-[#0A0A0F] hover:text-[#0A0A0F]"
                            }`}
                          >
                            {r.reviewed ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                            {r.reviewed ? "Reviewed" : "Mark reviewed"}
                          </button>
                        </td>
                        <td className="px-5 py-3">
                          {r.has_pdf ? (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/pre-agreements/${r.id}/pdf`}
                              data-testid={`pre-agreement-pdf-${r.contract_number}`}
                              onClick={async (e) => {
                                // Bearer-token download via fetch+blob (so the link works with auth)
                                e.preventDefault();
                                const res = await apiFetch(`/api/admin/pre-agreements/${r.id}/pdf`);
                                if (!res.ok) {
                                  alert("Could not download PDF");
                                  return;
                                }
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `${r.contract_number}.pdf`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                              className="inline-flex items-center gap-1.5 text-[#0A0A0F] underline underline-offset-2 font-medium"
                            >
                              Download
                            </a>
                          ) : (
                            <span className="text-[#9B9DA4]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => deletePreAgreement(r)}
                            data-testid={`pre-agreement-delete-${r.contract_number}`}
                            aria-label={`Delete ${r.contract_number}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#E8E7E2] text-[#62646B] hover:text-red-600 hover:border-red-300 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Verification call checklist modal */}
        {contactModal && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50" data-testid="contact-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><PhoneCall size={18} /> Verification call</h3>
                  <p className="text-sm text-[#62646B] mt-1">
                    {contactModal.contract_number} · {contactModal.buyer_full_name}. Every point below is <strong>mandatory</strong>. Completing it <strong>unlocks signing</strong> for the buyer.
                  </p>
                </div>
                <button type="button" onClick={() => setContactModal(null)} aria-label="Close" className="w-8 h-8 rounded-full hover:bg-[#F4F2EC] flex items-center justify-center shrink-0"><X size={16} /></button>
              </div>

              {contactModal.consents && (
                <div className="mt-4 p-3 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] text-xs text-[#62646B]" data-testid="contact-consents">
                  <p className="font-semibold text-[#0A0A0F] mb-1">Policy acceptance on file</p>
                  {["privacy", "terms"].map((k) => {
                    const cc = contactModal.consents[k];
                    if (!cc) return null;
                    return (
                      <p key={k} className="flex items-center gap-1.5">
                        <ShieldCheck size={11} className="text-[#2F6B33]" />
                        <span className="capitalize">{k === "privacy" ? "Privacy Policy" : "Terms & Conditions"}</span>
                        <span>· v{cc.version || "—"}</span>
                        <span>· {cc.accepted_at ? new Date(cc.accepted_at).toLocaleString() : "—"}</span>
                      </p>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div>
                  <label className="block text-xs font-semibold text-[#62646B] mb-1.5">Conducted by</label>
                  <input
                    type="text"
                    value={contactConductedBy}
                    onChange={(e) => setContactConductedBy(e.target.value)}
                    data-testid="contact-conducted-by-input"
                    className="w-full px-3 py-2 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#62646B] mb-1.5">Call language</label>
                  <select
                    value={contactCallLang}
                    onChange={(e) => setContactCallLang(e.target.value)}
                    data-testid="contact-language-select"
                    className="w-full px-3 py-2 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors text-sm"
                  >
                    <option value="en">English</option>
                    <option value="sr">Srpski</option>
                  </select>
                </div>
              </div>

              <p className="text-xs uppercase tracking-widest text-[#62646B] font-semibold mt-5 mb-1.5">Payment method (locked across all 24 instalments)</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "card", label: "Card", icon: <CreditCard size={13} /> },
                  { key: "sepa", label: "Bank transfer (SEPA)", icon: <Landmark size={13} /> },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setContactPayMethod(m.key)}
                    data-testid={`contact-paymethod-${m.key}`}
                    aria-pressed={contactPayMethod === m.key}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${contactPayMethod === m.key ? "border-[#0A0A0F] bg-[#FBF8F1]" : "border-[#E8E7E2] hover:border-[#9B9DA4]"}`}
                  >
                    {m.icon} {m.label}
                  </button>
                ))}
              </div>

              <p className="text-xs uppercase tracking-widest text-[#62646B] font-semibold mt-5 mb-2">1 · Data verified for the contract</p>
              <div className="space-y-1.5">
                {VERIFICATION_POINTS.map((p) => (
                  <label key={p.key} data-testid={`contact-verif-${p.key}`} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-[#FAFAF7] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!contactVerif[p.key]}
                      onChange={(e) => setContactVerif((c) => ({ ...c, [p.key]: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 shrink-0 accent-[#0A0A0F]"
                    />
                    <span className="text-sm text-[#0A0A0F]">{p.label}</span>
                  </label>
                ))}
              </div>

              <p className="text-xs uppercase tracking-widest text-[#62646B] font-semibold mt-5 mb-2">2 · Confirmation of understanding</p>
              <div className="space-y-1.5">
                {UNDERSTANDING_POINTS.map((p) => (
                  <label key={p.key} data-testid={`contact-understand-${p.key}`} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-[#FAFAF7] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!contactUnderstand[p.key]}
                      onChange={(e) => setContactUnderstand((c) => ({ ...c, [p.key]: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 shrink-0 accent-[#0A0A0F]"
                    />
                    <span className="text-sm text-[#0A0A0F]">{p.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex items-center justify-end gap-2 mt-6 sticky bottom-0 bg-white pt-3">
                <button type="button" onClick={() => setContactModal(null)} className="px-4 py-2 rounded-full text-sm font-medium border border-[#E8E7E2] hover:border-[#0A0A0F] transition-colors">Cancel</button>
                <button
                  type="button"
                  onClick={submitContact}
                  disabled={contactSaving || !allContactChecked}
                  data-testid="contact-submit"
                  className="px-5 py-2 rounded-full text-sm font-semibold bg-[#0A0A0F] text-white hover:bg-[#1F1F26] transition-colors disabled:opacity-40"
                >
                  {contactSaving ? "Saving…" : "Complete & unlock signing"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Correct buyer details modal */}
        {buyerModal && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50" data-testid="buyer-edit-modal">
            <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Pencil size={18} /> Correct buyer details</h3>
                  <p className="text-sm text-[#62646B] mt-1">
                    {buyerModal.contract_number}. Fixes an error found on the call. The WI-RZ number is kept; every change is audited.
                  </p>
                </div>
                <button type="button" onClick={() => setBuyerModal(null)} aria-label="Close" className="w-8 h-8 rounded-full hover:bg-[#F4F2EC] flex items-center justify-center shrink-0"><X size={16} /></button>
              </div>
              {buyerModal._loading ? (
                <p className="text-sm text-[#62646B] mt-6">Loading…</p>
              ) : (
                <>
                  <div className="mt-5 space-y-3">
                    {BUYER_FIELDS.map((f) => (
                      <div key={f.key}>
                        <label className="block text-xs font-semibold text-[#62646B] mb-1.5">{f.label}</label>
                        <input
                          type="text"
                          value={buyerForm[f.key] || ""}
                          onChange={(e) => setBuyerForm((b) => ({ ...b, [f.key]: e.target.value }))}
                          data-testid={`buyer-edit-${f.key}`}
                          className="w-full px-3 py-2 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-6">
                    <button type="button" onClick={() => setBuyerModal(null)} className="px-4 py-2 rounded-full text-sm font-medium border border-[#E8E7E2] hover:border-[#0A0A0F] transition-colors">Cancel</button>
                    <button
                      type="button"
                      onClick={submitBuyerEdit}
                      disabled={buyerSaving}
                      data-testid="buyer-edit-submit"
                      className="px-5 py-2 rounded-full text-sm font-semibold bg-[#0A0A0F] text-white hover:bg-[#1F1F26] transition-colors disabled:opacity-50"
                    >
                      {buyerSaving ? "Saving…" : "Save corrections"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <section data-testid="admin-reservations" className="mt-12">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Reservations</h2>
            <span className="text-xs text-[#62646B]">{reservations.length} total</span>
          </div>

          <div className="mt-5 bg-white border border-[#E8E7E2] rounded-2xl overflow-hidden">
            {reservations.length === 0 ? (
              <p className="p-6 text-sm text-[#62646B]">No reservations yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAF7] text-xs uppercase tracking-widest text-[#62646B] text-left">
                    <tr>
                      <th className="px-5 py-3 font-semibold">Date</th>
                      <th className="px-5 py-3 font-semibold">Name</th>
                      <th className="px-5 py-3 font-semibold">Email</th>
                      <th className="px-5 py-3 font-semibold">Phone</th>
                      <th className="px-5 py-3 font-semibold">Package</th>
                      <th className="px-5 py-3 font-semibold">Lang</th>
                      <th className="px-5 py-3 font-semibold">Message</th>
                      <th className="px-5 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.map((r) => (
                      <tr
                        key={r.id}
                        data-testid={`reservation-row-${r.id}`}
                        className="border-t border-[#E8E7E2] hover:bg-[#FAFAF7] transition-colors"
                      >
                        <td className="px-5 py-3 whitespace-nowrap tabular-nums text-[#62646B]">
                          {new Date(r.timestamp).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 font-medium">{r.name}</td>
                        <td className="px-5 py-3">
                          <a href={`mailto:${r.email}`} className="text-[#0A0A0F] underline underline-offset-2">
                            {r.email}
                          </a>
                        </td>
                        <td className="px-5 py-3 text-[#62646B]">{r.phone || "—"}</td>
                        <td className="px-5 py-3">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-[#F4F2EC] text-xs font-medium">
                            {r.package}
                          </span>
                        </td>
                        <td className="px-5 py-3 uppercase text-xs tracking-widest text-[#62646B]">{r.language}</td>
                        <td className="px-5 py-3 text-[#62646B] max-w-xs truncate" title={r.message || ""}>
                          {r.message || "—"}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => deleteReservation(r)}
                            data-testid={`reservation-delete-${r.id}`}
                            aria-label={`Delete reservation from ${r.name || r.email}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#E8E7E2] text-[#62646B] hover:text-red-600 hover:border-red-300 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {analytics && (
          <p className="text-xs text-[#9B9DA4] mt-10">
            Generated {new Date(analytics.generated_at).toLocaleString()}
          </p>
        )}
      </main>
    </div>
  );
};

// ---------- Container ----------
export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());

  // Verify any existing token on mount
  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        const res = await apiFetch("/api/admin/me");
        if (res.ok) setAuthed(true);
        else {
          setToken(null);
          setAuthed(false);
        }
      } catch {
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const handleLogout = () => {
    setToken(null);
    setAuthed(false);
    navigate("/admin", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
        <p className="text-sm text-[#62646B]">Checking session…</p>
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={handleLogout} />
  ) : (
    <Login onLogin={() => setAuthed(true)} />
  );
}
